import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ChevronDown, Menu, X } from 'lucide-react';

const Header = () => {
  const { user, isAdmin, logout } = useAuth();
  const location = useLocation();

  const [cityOpen, setCityOpen] = useState(false);
  const [dashOpen, setDashOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const cityRef = useRef(null);
  const dashRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (cityRef.current && !cityRef.current.contains(e.target)) setCityOpen(false);
      if (dashRef.current && !dashRef.current.contains(e.target)) setDashOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close everything on route change
  useEffect(() => {
    setCityOpen(false);
    setDashOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const isCityActive = ['/council', '/relay', '/audit', '/nonprofits'].includes(location.pathname);
  const isDashActive = ['/dashboard', '/account'].includes(location.pathname);

  // ── Mobile menu link ─────────────────────────────────────────────────────────
  const MobileLink = ({ to, label, color }) => (
    <Link
      to={to}
      className={`block px-4 py-3 font-display text-sm tracking-wider border-b border-[#1a1a1a] transition-colors ${
        color ? color :
        isActive(to) ? 'text-[#C5A52A] bg-[#111]' : 'text-gray-300 hover:text-white hover:bg-[#111]'
      }`}
    >
      {label}
    </Link>
  );

  return (
    <header className="bg-black border-b border-[#1a1a1a] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3 gap-2">

          {/* Logo */}
          <Link to="/" className="flex items-center hover:opacity-90 transition-opacity flex-shrink-0">
            <img
              src="/logo.png"
              alt="CYQR.CA"
              className="h-10 sm:h-12 lg:h-14 w-auto"
            />
          </Link>

          {/* ── DESKTOP nav (hidden on mobile) ──────────────────────────────── */}
          <nav className="hidden md:flex items-center gap-4 lg:gap-6">

            <Link to="/" className={`font-display text-xs lg:text-sm tracking-wider transition-colors flex-shrink-0 ${isActive('/') ? 'text-[#C5A52A]' : 'text-gray-400 hover:text-white'}`}>THE FEED</Link>

            <Link
              to="/badges"
              className={`font-display text-xs lg:text-sm tracking-wider transition-colors flex-shrink-0 ${
                location.pathname.startsWith('/badges') || location.pathname === '/leaderboard' || location.pathname === '/ranks'
                  ? 'text-[#C5A52A]' : 'text-gray-400 hover:text-white'
              }`}
            >
              THE BADGE
            </Link>

            {/* THE CITY dropdown */}
            <div className="relative flex-shrink-0" ref={cityRef}>
              <button
                onClick={() => { setCityOpen(v => !v); setDashOpen(false); }}
                className={`flex items-center gap-1 font-display text-xs lg:text-sm tracking-wider transition-colors ${isCityActive ? 'text-[#C5A52A]' : 'text-gray-400 hover:text-white'}`}
              >
                THE CITY <ChevronDown className="w-3 h-3" />
              </button>
              {cityOpen && (
                <div className="absolute top-full left-0 mt-1 w-44 bg-[#111] border border-[#2a2a2a] shadow-xl z-[100]">
                  {[
                    { to: '/council', label: 'COUNCIL' },
                    { to: '/relay', label: 'RELAY' },
                    { to: '/audit', label: 'AUDIT' },
                    { to: '/nonprofits', label: 'NON-PROFITS', color: 'text-red-500' },
                    { to: '/qcca', label: 'CONDO OWNERS', color: 'text-[#C5A52A]' },
                  ].map(({ to, label, color }) => (
                    <React.Fragment key={to}>
                      <Link
                        to={to}
                        onClick={() => setCityOpen(false)}
                        className={`block px-4 py-3 font-display text-xs tracking-wider transition-colors ${
                          color ? `${color} hover:opacity-80 hover:bg-[#1a1a1a]` :
                          isActive(to) ? 'text-[#C5A52A] bg-[#1a1a1a]' : 'text-gray-400 hover:text-white hover:bg-[#1a1a1a]'
                        }`}
                      >
                        {label}
                      </Link>
                      <div className="border-t border-[#2a2a2a] last:hidden" />
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>

            <Link to="/store" className={`font-display text-xs lg:text-sm tracking-wider transition-colors flex-shrink-0 ${isActive('/store') ? 'text-[#C5A52A]' : 'text-gray-400 hover:text-white'}`}>STORE</Link>

            {/* DASHBOARD dropdown or LOGIN */}
            {user ? (
              <div className="relative flex-shrink-0" ref={dashRef}>
                <button
                  onClick={() => { setDashOpen(v => !v); setCityOpen(false); }}
                  className={`flex items-center gap-1 font-display text-xs lg:text-sm tracking-wider transition-colors ${isDashActive ? 'text-[#C5A52A]' : 'text-gray-400 hover:text-white'}`}
                >
                  DASHBOARD <ChevronDown className="w-3 h-3" />
                </button>
                {dashOpen && (
                  <div className="absolute top-full right-0 mt-1 w-44 bg-[#111] border border-[#2a2a2a] shadow-xl z-[100]">
                    <Link to="/dashboard" onClick={() => setDashOpen(false)} className={`block px-4 py-3 font-display text-xs tracking-wider transition-colors ${isActive('/dashboard') ? 'text-[#C5A52A] bg-[#1a1a1a]' : 'text-gray-400 hover:text-white hover:bg-[#1a1a1a]'}`}>MY DASHBOARD</Link>
                    <div className="border-t border-[#2a2a2a]" />
                    <Link to="/account" onClick={() => setDashOpen(false)} className={`block px-4 py-3 font-display text-xs tracking-wider transition-colors ${isActive('/account') ? 'text-[#C5A52A] bg-[#1a1a1a]' : 'text-gray-400 hover:text-white hover:bg-[#1a1a1a]'}`}>MY ACCOUNT</Link>
                    {isAdmin() && (
                      <>
                        <div className="border-t border-[#2a2a2a]" />
                        <Link to="/admin" onClick={() => setDashOpen(false)} className="block px-4 py-3 font-display text-xs tracking-wider text-green-500 hover:text-green-400 hover:bg-[#1a1a1a] transition-colors">ADMIN</Link>
                      </>
                    )}
                    <div className="border-t border-[#2a2a2a]" />
                    <button onClick={() => { setDashOpen(false); logout(); }} className="w-full text-left px-4 py-3 font-display text-xs tracking-wider text-gray-500 hover:text-white hover:bg-[#1a1a1a] transition-colors">LOGOUT</button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="font-display text-xs lg:text-sm tracking-wider text-gray-400 hover:text-white transition-colors flex-shrink-0">LOGIN</Link>
            )}

            {/* SUBMIT */}
            <Link to="/submit" className="bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-display text-xs lg:text-sm py-2 px-3 lg:px-4 tracking-wider transition-colors flex-shrink-0" data-testid="submit-btn">
              SUBMIT
            </Link>
          </nav>

          {/* ── MOBILE right side: SUBMIT + hamburger ──────────────────────── */}
          <div className="flex md:hidden items-center gap-2">
            <Link to="/submit" className="bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-display text-xs py-2 px-3 tracking-wider transition-colors" data-testid="submit-btn-mobile">
              SUBMIT
            </Link>
            <button
              onClick={() => setMobileOpen(v => !v)}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              aria-label="Menu"
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── MOBILE slide-down menu ─────────────────────────────────────────── */}
      {mobileOpen && (
        <div className="md:hidden bg-black border-t border-[#1a1a1a]" data-testid="mobile-menu">
          <MobileLink to="/" label="THE FEED" />
          <MobileLink to="/badges" label="THE BADGE" />

          {/* THE CITY section */}
          <div className="px-4 py-2 text-[10px] font-mono text-gray-600 uppercase tracking-widest bg-[#0a0a0a] border-b border-[#1a1a1a]">THE CITY</div>
          <MobileLink to="/council" label="  COUNCIL" />
          <MobileLink to="/relay" label="  RELAY" />
          <MobileLink to="/audit" label="  AUDIT" />
          <MobileLink to="/nonprofits" label="  NON-PROFITS" color="text-red-500 block px-4 py-3 font-display text-sm tracking-wider border-b border-[#1a1a1a]" />
          <MobileLink to="/qcca" label="  CONDO OWNERS" />

          <MobileLink to="/store" label="STORE" />

          {user ? (
            <>
              <div className="px-4 py-2 text-[10px] font-mono text-gray-600 uppercase tracking-widest bg-[#0a0a0a] border-b border-[#1a1a1a]">ACCOUNT</div>
              <MobileLink to="/dashboard" label="  MY DASHBOARD" />
              <MobileLink to="/account" label="  MY ACCOUNT" />
              {isAdmin() && <MobileLink to="/admin" label="  ADMIN" color="text-green-500 block px-4 py-3 font-display text-sm tracking-wider border-b border-[#1a1a1a]" />}
              <button
                onClick={() => { setMobileOpen(false); logout(); }}
                className="w-full text-left px-4 py-3 font-display text-sm tracking-wider text-gray-500 border-b border-[#1a1a1a] hover:text-white hover:bg-[#111] transition-colors"
              >
                LOGOUT
              </button>
            </>
          ) : (
            <MobileLink to="/login" label="LOGIN" />
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
